import type { MivoTranslationType } from "@mivo/i18n";

import { ApiMessageCodes, type ApiMessageCode } from "./apiMessageCodes";
import { SESSION_EXPIRED_TRANSLATIONS } from "./sessionExpiredConstants";

/**
 * Client translation objects for Phase A API message codes.
 * Keys are wire `code` values (snake_case strings). Prefer via `getTranslation` in T1.4+.
 * pt-BR aligns with existing backend `detail` strings where known.
 * @see docs/domains/platform/backendI18n/phaseACodes.md
 */
export const API_MESSAGE_TRANSLATIONS = {
  [ApiMessageCodes.trialExpired]: {
    "pt-BR": "Seu período de experimentação encerrou. Assine o plano Básico para continuar.",
    en: "Your trial period has ended. Subscribe to the Basic plan to continue.",
    es: "Tu período de prueba terminó. Suscríbete al plan Básico para continuar.",
    de: "Ihr Testzeitraum ist abgelaufen. Abonnieren Sie den Basic-Plan, um fortzufahren.",
  },
  [ApiMessageCodes.paymentRequired]: {
    "pt-BR": "Conclua a assinatura do plano escolhido para continuar.",
    en: "Complete the subscription for your chosen plan to continue.",
    es: "Completa la suscripción del plan elegido para continuar.",
    de: "Schließen Sie das Abonnement für den von Ihnen gewählten Plan ab, um fortfahren zu können.",
  },
  [ApiMessageCodes.billingOnly]: {
    "pt-BR": "Sua conta está em modo somente cobrança. Acesse Assinatura para regularizar.",
    en: "Your account is in billing-only mode. Go to Subscription to resolve this.",
    es: "Tu cuenta está en modo solo facturación. Ve a Suscripción para regularizar.",
    de: "Ihr Konto befindet sich im reinen Abrechnungsmodus. Gehen Sie zu „Abonnement“, um dieses Problem zu beheben.",
  },
  [ApiMessageCodes.orgBillingBlocked]: {
    "pt-BR": "O acesso clínico está bloqueado pelo faturamento da organização. Regularize a assinatura para continuar.",
    en: "Clinical access is blocked by organization billing. Resolve the subscription to continue.",
    es: "El acceso clínico está bloqueado por la facturación de la organización. Regulariza la suscripción para continuar.",
    de: "Der klinische Zugang wird durch die Abrechnung der Organisation blockiert. Lösen Sie das Abonnement auf, um fortzufahren.",
  },
  [ApiMessageCodes.accountClosed]: {
    "pt-BR": "Conta inativa.",
    en: "Account inactive.",
    es: "Cuenta inactiva.",
    de: "Konto inaktiv.",
  },
  [ApiMessageCodes.notAuthenticated]: {
    "pt-BR": "Não autenticado.",
    en: "Not authenticated.",
    es: "No autenticado.",
    de: "Nicht authentifiziert.",
  },
  [ApiMessageCodes.forbidden]: {
    "pt-BR": "Você não tem permissão para esta ação.",
    en: "You do not have permission for this action.",
    es: "No tienes permiso para esta acción.",
    de: "Sie haben keine Berechtigung für diese Aktion.",
  },
  [ApiMessageCodes.consentRequired]: {
    "pt-BR":
      "É necessário aceitar a versão atual dos Termos de Uso e da Política de Privacidade para continuar.",
    en: "You must accept the current Terms of Use and Privacy Policy to continue.",
    es: "Debes aceptar la versión actual de los Términos de Uso y la Política de Privacidad para continuar.",
    de: "Sie müssen die aktuellen Nutzungsbedingungen und Datenschutzbestimmungen akzeptieren, um fortfahren zu können.",
  },
  [ApiMessageCodes.planAdjustmentBasic]: {
    "pt-BR":
      "Seu plano Básico inclui 1 acesso Financeiro (titular) e não inclui Time administrativo nem armazenamento de fotos e vídeos. Ajustamos sua equipe automaticamente.",
    en:
      "Your Basic plan includes 1 Finance access (account holder) and does not include administrative team or photo and video storage. We adjusted your team automatically.",
    es:
      "Tu plan Básico incluye 1 acceso a Finanzas (titular) y no incluye equipo administrativo ni almacenamiento de fotos y videos. Ajustamos tu equipo automáticamente.",
    de: "Ihr Basic-Plan beinhaltet 1 Finanzzugang (Kontoinhaber) und beinhaltet kein Verwaltungsteam oder Foto- und Videospeicher. Wir haben Ihr Team automatisch angepasst.",
  },
  [ApiMessageCodes.planAdjustmentFree]: {
    "pt-BR":
      "Seu plano Gratuito não inclui Time administrativo, Módulo Financeiro nem armazenamento de fotos e vídeos. Ajustamos sua equipe automaticamente.",
    en:
      "Your Free plan does not include administrative team, Finance module, or photo and video storage. We adjusted your team automatically.",
    es:
      "Tu plan Gratuito no incluye equipo administrativo, módulo de Finanzas ni almacenamiento de fotos y videos. Ajustamos tu equipo automáticamente.",
    de: "Ihr kostenloser Plan beinhaltet kein Verwaltungsteam, kein Finanzmodul sowie keine Foto- und Videospeicherung. Wir haben Ihr Team automatisch angepasst.",
  },
  [ApiMessageCodes.planAdjustmentGeneric]: {
    "pt-BR": "Seu plano foi ajustado. Revise sua equipe e os módulos disponíveis.",
    en: "Your plan was adjusted. Review your team and available modules.",
    es: "Tu plan fue ajustado. Revisa tu equipo y los módulos disponibles.",
    de: "Ihr Plan wurde angepasst. Überprüfen Sie Ihr Team und die verfügbaren Module.",
  },
  [ApiMessageCodes.planLimitPatients]: {
    "pt-BR": "Limite de pacientes do plano atingido.",
    en: "Patient limit for your plan has been reached.",
    es: "Se alcanzó el límite de pacientes del plan.",
    de: "Das Patientenlimit für Ihren Plan wurde erreicht.",
  },
  [ApiMessageCodes.planStorageUnavailable]: {
    "pt-BR": "Armazenamento de arquivos não disponível no plano Básico.",
    en: "File storage is not available on the Basic plan.",
    es: "El almacenamiento de archivos no está disponible en el plan Básico.",
    de: "Dateispeicherung ist im Basic-Plan nicht verfügbar.",
  },
  [ApiMessageCodes.planStorageLimit]: {
    "pt-BR": "Limite de armazenamento do plano atingido.",
    en: "Storage limit for your plan has been reached.",
    es: "Se alcanzó el límite de almacenamiento del plan.",
    de: "Das Speicherlimit für Ihren Plan wurde erreicht.",
  },
  [ApiMessageCodes.moduleFinanceUnavailable]: {
    "pt-BR": "O Financeiro não está disponível no seu plano atual.",
    en: "Finance is not available on your current plan.",
    es: "Finanzas no está disponible en tu plan actual.",
    de: "Die Finanzierung ist in Ihrem aktuellen Plan nicht verfügbar.",
  },
  [ApiMessageCodes.moduleSecretaryUnavailable]: {
    "pt-BR": "Time administrativo disponível no plano Pro.",
    en: "Administrative team is available on the Pro plan.",
    es: "El equipo administrativo está disponible en el plan Pro.",
    de: "Das Verwaltungsteam steht im Pro-Plan zur Verfügung.",
  },
  [ApiMessageCodes.moduleAiReportsUnavailable]: {
    "pt-BR": "O módulo Relatórios por IA está disponível no Marketplace. Ative-o para continuar.",
    en: "The AI Reports module is available in the Marketplace. Activate it to continue.",
    es: "El módulo Informes por IA está disponible en el Marketplace. Actívalo para continuar.",
    de: "Das AI Reports-Modul ist im Marketplace verfügbar. Aktivieren Sie es, um fortzufahren.",
  },
  [ApiMessageCodes.catalogPlanUnavailable]: {
    "pt-BR": "Plano indisponível no momento.",
    en: "Plan unavailable at the moment.",
    es: "Plan no disponible en este momento.",
    de: "Plan derzeit nicht verfügbar.",
  },
  [ApiMessageCodes.catalogModuleUnavailable]: {
    "pt-BR": "Módulo indisponível no momento.",
    en: "Module unavailable at the moment.",
    es: "Módulo no disponible en este momento.",
    de: "Modul derzeit nicht verfügbar.",
  },
  [ApiMessageCodes.billingPaymentFailed]: {
    "pt-BR": "Não foi possível processar o pagamento. Verifique os dados e tente novamente.",
    en: "Could not process the payment. Check your details and try again.",
    es: "No se pudo procesar el pago. Verifica los datos e inténtalo de nuevo.",
    de: "Die Zahlung konnte nicht verarbeitet werden. Überprüfen Sie Ihre Angaben und versuchen Sie es erneut.",
  },
  [ApiMessageCodes.billingPaymentMethodChangeFailed]: {
    "pt-BR": "Não foi possível alterar a forma de pagamento. Tente novamente.",
    en: "Could not change the payment method. Please try again.",
    es: "No se pudo cambiar la forma de pago. Inténtalo de nuevo.",
    de: "Die Zahlungsmethode konnte nicht geändert werden. Bitte versuchen Sie es erneut.",
  },
  [ApiMessageCodes.billingTrialCancelUnavailable]: {
    "pt-BR": "Cancelamento de teste disponível apenas durante o período de experimentação.",
    en: "Trial cancellation is only available during the trial period.",
    es: "La cancelación de la prueba solo está disponible durante el período de prueba.",
    de: "Die Kündigung der Testversion ist nur während des Testzeitraums möglich.",
  },
  [ApiMessageCodes.conflict]: {
    "pt-BR": "Os dados foram alterados por outra ação. Recarregue e tente novamente.",
    en: "The data was changed by another action. Reload and try again.",
    es: "Los datos fueron modificados por otra acción. Recargue e intente de nuevo.",
    de: "Die Daten wurden durch eine andere Aktion geändert. Bitte neu laden und erneut versuchen.",
  },
  [ApiMessageCodes.internalError]: {
    "pt-BR": "Ocorreu um erro inesperado. Tente novamente.",
    en: "An unexpected error occurred. Please try again.",
    es: "Ocurrió un error inesperado. Inténtelo de nuevo.",
    de: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
  },
  [ApiMessageCodes.sessionExpired]: SESSION_EXPIRED_TRANSLATIONS.toast,
  [ApiMessageCodes.authInvalidCredentials]: {
    "pt-BR": "E-mail ou senha inválidos.",
    en: "Invalid email or password.",
    es: "Correo o contraseña inválidos.",
    de: "Ungültige E-Mail-Adresse oder ungültiges Passwort.",
  },
  [ApiMessageCodes.authEmailAlreadyRegistered]: {
    "pt-BR": "Este e-mail já possui conta.",
    en: "Email already registered.",
    es: "Este correo ya tiene una cuenta.",
    de: "E-Mail bereits registriert.",
  },
  [ApiMessageCodes.authNameTooShort]: {
    "pt-BR": "Nome muito curto.",
    en: "Name is too short.",
    es: "El nombre es demasiado corto.",
    de: "Der Name ist zu kurz.",
  },
  [ApiMessageCodes.authInvalidPreferredLocale]: {
    "pt-BR": "Idioma selecionado não é suportado. Escolha um idioma disponível ou recarregue a página.",
    en: "The selected language is not supported. Choose an available language or refresh the page.",
    es: "El idioma seleccionado no es compatible. Elige un idioma disponible o recarga la página.",
    de: "Die ausgewählte Sprache wird nicht unterstützt. Wählen Sie eine verfügbare Sprache oder laden Sie die Seite neu.",
  },
  [ApiMessageCodes.authInviteInvalid]: {
    "pt-BR": "Convite inválido ou expirado.",
    en: "Invalid or expired invite.",
    es: "Invitación inválida o expirada.",
    de: "Ungültige oder abgelaufene Einladung.",
  },
  [ApiMessageCodes.authInviteTokenRequired]: {
    "pt-BR": "Token de convite obrigatório.",
    en: "Invite token is required.",
    es: "Se requiere el token de invitación.",
    de: "Ein Einladungstoken ist erforderlich.",
  },
  [ApiMessageCodes.authEmailConfirmFailed]: {
    "pt-BR": "Não foi possível confirmar o e-mail. Solicite um novo código e tente novamente.",
    en: "Could not confirm the email. Request a new code and try again.",
    es: "No se pudo confirmar el correo. Solicita un nuevo código e inténtalo de nuevo.",
    de: "Die E-Mail konnte nicht bestätigt werden. Fordern Sie einen neuen Code an und versuchen Sie es erneut.",
  },
  [ApiMessageCodes.authEmailRequired]: {
    "pt-BR": "Informe o e-mail.",
    en: "Enter your email.",
    es: "Ingresa el correo electrónico.",
    de: "Geben Sie Ihre E-Mail-Adresse ein.",
  },
  [ApiMessageCodes.authEmailInvalid]: {
    "pt-BR": "Informe um e-mail válido.",
    en: "Enter a valid email address.",
    es: "Ingresa un correo electrónico válido.",
    de: "Geben Sie eine gültige E-Mail-Adresse ein.",
  },
  [ApiMessageCodes.authNameRequired]: {
    "pt-BR": "Informe o nome completo.",
    en: "Enter your full name.",
    es: "Ingresa el nombre completo.",
    de: "Geben Sie Ihren vollständigen Namen ein.",
  },
  [ApiMessageCodes.authPasswordRequired]: {
    "pt-BR": "Informe a senha.",
    en: "Enter your password.",
    es: "Ingresa la contraseña.",
    de: "Geben Sie Ihr Passwort ein.",
  },
  [ApiMessageCodes.authPasswordWeak]: {
    "pt-BR": "A senha deve ter pelo menos 8 caracteres, incluindo letras e números.",
    en: "Password must be at least 8 characters and include letters and numbers.",
    es: "La contraseña debe tener al menos 8 caracteres e incluir letras y números.",
    de: "Das Passwort muss mindestens 8 Zeichen lang sein und Buchstaben sowie Ziffern enthalten.",
  },
  [ApiMessageCodes.authSpecialtyInvalid]: {
    "pt-BR": "Especialidade inválida.",
    en: "Invalid specialty.",
    es: "Especialidad inválida.",
    de: "Ungültige Spezialität.",
  },
  [ApiMessageCodes.authSpecialtyImmutable]: {
    "pt-BR": "A especialidade não pode ser alterada. Contate o suporte.",
    en: "Specialty cannot be changed. Contact support.",
    es: "La especialidad no se puede cambiar. Contacta al soporte.",
    de: "Fachrichtung kann nicht geändert werden. Kontaktieren Sie den Support.",
  },
  [ApiMessageCodes.authClinicRegisterUnavailable]: {
    "pt-BR": "Cadastro de clínica temporariamente indisponível.",
    en: "Clinic registration is temporarily unavailable.",
    es: "El registro de clínica no está disponible temporalmente.",
    de: "Die Registrierung in der Klinik ist vorübergehend nicht möglich.",
  },
  [ApiMessageCodes.authRegistrationTypeInvalid]: {
    "pt-BR": "Tipo de cadastro inválido.",
    en: "Invalid registration type.",
    es: "Tipo de registro inválido.",
    de: "Ungültiger Registrierungstyp.",
  },
  [ApiMessageCodes.authSignupPlanInvalid]: {
    "pt-BR": "Plano de cadastro inválido.",
    en: "Invalid signup plan.",
    es: "Plan de registro inválido.",
    de: "Ungültiger Anmeldeplan.",
  },
  [ApiMessageCodes.clinicContextMissing]: {
    "pt-BR": "Contexto de clínica não encontrado.",
    en: "Clinic context not found.",
    es: "No se encontró el contexto de la clínica.",
    de: "Klinikkontext nicht gefunden.",
  },
  [ApiMessageCodes.clinicForbidden]: {
    "pt-BR": "Sem permissão para esta ação.",
    en: "You do not have permission for this action.",
    es: "Sin permiso para esta acción.",
    de: "Sie haben keine Berechtigung für diese Aktion.",
  },
  [ApiMessageCodes.clinicMemberNotFound]: {
    "pt-BR": "Membro não encontrado.",
    en: "Member not found.",
    es: "Miembro no encontrado.",
    de: "Mitglied nicht gefunden.",
  },
  [ApiMessageCodes.clinicMemberNotTherapist]: {
    "pt-BR": "Membro não é profissional.",
    en: "Member is not a professional.",
    es: "El miembro no es un profesional.",
    de: "Das Mitglied ist kein Profi.",
  },
  [ApiMessageCodes.clinicMemberAlreadyActive]: {
    "pt-BR": "Membro já está ativo.",
    en: "Member is already active.",
    es: "El miembro ya está activo.",
    de: "Mitglied ist bereits aktiv.",
  },
  [ApiMessageCodes.clinicCannotDeactivateSelf]: {
    "pt-BR": "Você não pode desativar a si mesmo.",
    en: "You cannot deactivate yourself.",
    es: "No puedes desactivarte a ti mismo.",
    de: "Sie können sich nicht selbst deaktivieren.",
  },
  [ApiMessageCodes.clinicPrimaryPatientsBlockDeactivate]: {
    "pt-BR":
      "Este profissional ainda é responsável primário por pacientes. Reatribua antes de desativar.",
    en: "This professional is still the primary caregiver for patients. Reassign before deactivating.",
    es: "Este profesional aún es el responsable principal de pacientes. Reasigna antes de desactivar.",
    de: "Dieser Fachmann ist immer noch die primäre Pflegekraft für die Patienten. Vor der Deaktivierung neu zuweisen.",
  },
  [ApiMessageCodes.clinicMemberRolesRequired]: {
    "pt-BR": "Este membro não possui papéis na equipe. Atribua papéis antes de reativar.",
    en: "This member has no team roles. Assign roles before reactivating.",
    es: "Este miembro no tiene roles en el equipo. Asigna roles antes de reactivar.",
    de: "Dieses Mitglied hat keine Teamrollen. Weisen Sie vor der Reaktivierung Rollen zu.",
  },
  [ApiMessageCodes.contaForbiddenClinicStaff]: {
    "pt-BR":
      "Cancelamento de conta não está disponível para membros da clínica. Saia da clínica ou fale com o administrador.",
    en: "Account cancellation is not available for clinic staff. Leave the clinic or contact the administrator.",
    es: "La cancelación de cuenta no está disponible para el personal de la clínica. Sal de la clínica o habla con el administrador.",
    de: "Kontokündigung ist für Klinikmitglieder nicht verfügbar. Verlassen Sie die Klinik oder wenden Sie sich an den Administrator.",
  },
  [ApiMessageCodes.contaRequiresAssinaturaCancel]: {
    "pt-BR": "Cancele a assinatura da clínica em Assinatura antes de cancelar a conta.",
    en: "Cancel the clinic subscription under Assinatura before cancelling the account.",
    es: "Cancela la suscripción de la clínica en Assinatura antes de cancelar la cuenta.",
    de: "Kündigen Sie das Klinikabonnement unter Assinatura, bevor Sie das Konto kündigen.",
  },
} as const satisfies Record<ApiMessageCode, MivoTranslationType>;

export type ApiMessageTranslations = typeof API_MESSAGE_TRANSLATIONS;
